import { Fragment } from "react";
import ModuleSection, { type Section } from "./components/ModuleSection";
import { usePageTitle } from "./hooks/usePageTitle";

const HEADER = `TerraStation II is TERRASCIENCES premier product, a modular software suite designed for use by geologists, petrophysicists and other professional earth scientists. The major modules are:`;

const SECTIONS: Section[] = [
  {
    title: "Base TerraStation (Mandatory)",
    intro:
      "The minimum system required. All other modules can be added to this basic platform.",
    bullets: [
      "Provides project management.",
      "Provides data import and export capabilities, including LIS, DLIS, LAS, ASCII formats.",
      "Directional survey loading and computation, including TVD, TVT and TST.",
      "Curve editing, splicing, shifting, base line shifting, and other utility functions (interpolation, rescaling, etc).",
      "Environmental corrections.",
      "Crossplotting, histograms, bar graphs, ternary diagrams.",
      "Curve normalizing.",
      "Single well composite log display generation using the IMAGELog module.",
      "Stereonet based Dip Analysis, including Azimuth Vector and Cumulative Dip capability.",
      "Pressure Modeling and stress analysis.",
      "Plot editing and plot montage creation.",
      "Creation of graphics output in various formats, including PDF, CGM, PostScript, EMF, BMP.",
    ],
  },
  {
    title: "Petrophysical Analysis",
    intro:
      "A highly flexible suite of capabilities for analyzing wireline data.<br/>Includes:",
    bullets: [
      "Loading from DLIS, LIS, LAS and other formats.",
      "Interactive data editing, depth shifting, curve splicing, along with many useful curve manipulation options.",
      "Pickett, Hingle, SP-Rwa, Thomas-Stieber, Zplots, pressure vs depth plots and many other crossplots.",
      "Full deterministic analysis including most Vshale, Porosity, and water saturation equations.",
      "Probabilistic modeling option for complex lithology scenarios.",
      "CMR T2 analysis.",
      "Core data handled on true core depths, not interpolated to wireline depths.",
      "Netpay analysis including a probabilistic capability.",
      "Temperature gradient analysis.",
      "Coal Bed Methane Analysis.",
      "A set of statistical analysis options including cluster analysis, Fourier analysis, multiple linear regression and more.",
    ],
  },
  {
    title: "Borehole Imaging",
    intro:
      "For image processing and interpretation we provide the most comprehensive system available.<br/>Functionality includes:",
    bullets: [
      "Handles all known imaging tools, both wireline and LWD/MWD by all logging companies.",
      "Speed corrections, button correlation, pad/flap correlation, swing-arm, dead/faulty button, and more.",
      "Quality control plots of magnetometers and acceleration information.",
      "Image depth shifting and splicing tools.",
      "User definable color maps.",
      "Image calibration to wireline curve.",
      "Static/dynamic normalization. Control windows and step size of dynamic normalization.",
      "Wide range of enhancement filters available.",
      "User definable pick classification schemes.",
      "Calculations include: Fracture density, fracture aperture, sand counts, and more.",
      "Full suite of dip analysis displays and tools - stereonets, tadpole plots, SCAT displays, Mohr diagrams.",
    ],
  },
  {
    title: "Cross section and well correlation",
    intro:
      "For the geologists we provide a fully functional cross section building module.<br/>Functionality includes:",
    bullets: [
      "Handles both straight hole and deviated wells in the same section.",
      "Allows sections based on TVD, TST, and TVTas well as measured depth.",
      "Build templates that are unique for each well in the section, allowing all down hole data including borehole image data to be displayed in section.",
      "Allows 300 wells per section.",
      "Insert pinchouts, faults, and other control points in the section.",
      "Interactively add and edit formation tops on the cross sections.",
      "Fence diagram capability.",
      "Interwell curve extrapolation.",
      "Gridding of surfaces and display of grided surface on cross section.",
    ],
  },
  {
    title: "Composite well log generation",
    intro:
      "The IMAGELog module within the basic TerraStation II package allows you to design and build templates that are used to create composite well displays.",
    bullets: [
      "Display well on any vertical reference, including TVD, TIME, TVT, TST.",
      "Choose from over 30 different track types including, curves, depth, color code curve, comment tracks, dipmeter, tadpole, rose diagrams, geologic age, and many more.",
      "Add a wide variety of data types including core data, completion data, core photographs, comments, sample descriptions, lithology, facies, dipmeter, and more.",
      "Build and use headers and footers for your final output.",
      "Output at any vertical scale to a wide variety of formats – PDF, CGM, HPGL2, Postscript, EMF.",
      "Interactively edit, depth shift curves and core.",
    ],
  },
  {
    title: "Mapping",
    intro:
      "A module for displaying base maps, and posting, contouring and analyzing surfaces and other data.",
    bullets: [
      "Contour maps with a variety of display options.",
      "Perspective block diagrams of up to 4 computed surfaces.",
      "Gridding, triangulation and kriging capability.",
      "Posting of well displays on map at well locations.",
      "Pie charts, starburst maps and other posting capabilities.",
      "Compute mappable values from log data using a variety of computation options.",
      "Surface to surface computation capabilities on both raw data and computed grids.",
      "Import and export of grids.",
    ],
  },
  {
    title: "Sonic Waveform Processing",
    intro:
      "A module for analyzing and processing full waveform sonic logs is available.<br />It includes:",
    bullets: [
      "Handles both monopole and dipole tools.",
      "Filters for noise removal, including bandpass filter.",
      "Semblance contouring function.",
      "Compute compressional, shear, and Stonely arrivals.",
      "Display instantaneous phase, amplitude, and frequency.",
      "Dipole Anisotropy computation.",
      "Frequency dispersion correction available.",
    ],
  },

];

function TerraStationOverview() {
  usePageTitle('TerraStation Overview')
  return (
    <div
      id="ts-terrastation-wrapper"
      className="w-full h-full flex items-center justify-center"
    >
      <div
        id="ts-terrastation-content"
        className="w-2/3 p-4 h-full flex flex-col items-center"
      >
        <div className="w-full relative flex items-center justify-center text-2xl p-2 border-b border-light-border">
          TerraStation Overview
        </div>

        <div className="w-full mt-4 text-left text-light-text">
          <strong dangerouslySetInnerHTML={{ __html: HEADER }} />

          {SECTIONS.map((s, idx) => (
            <Fragment key={idx}>
              <ModuleSection section={s} />
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TerraStationOverview;
